import React from 'react';
import ConfettiExplosion from 'react-confetti-explosion';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

import {
    Typography,
    Box,
    Card,
    CardContent,
    CardActions,
    Button,
    Chip,
} from '@mui/material';

import {
    Quiz as QuizIcon,
    Person as PersonIcon,
    CheckCircle as CheckIcon,
} from '@mui/icons-material';

export const CourseSearchCard = ({course, onJoinCourse, isJoining, isEnrolled, showConfetti}) => (
    <Box sx={{position: 'relative'}}>
        {showConfetti && (
            <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 1000,
                pointerEvents: 'none'
            }}>
                <ConfettiExplosion
                    particleCount={80}
                    width={900}
                    duration={3500}
                    force={0.6}
                />
            </Box>
        )}
        <Card
            variant="outlined"
            elevation={1}
            sx={{
                height: 320, // Fixed height for all cards
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.25s, box-shadow 0.25s',
            }}
        >
            <CardContent sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                pb: 1, // Reduce bottom padding to make room for CardActions
            }}>
                <Box sx={{display: 'flex', alignItems: 'center', mb: 1}}>
                    <Typography variant="h6" gutterBottom sx={{fontWeight: 'bold', mb: 0}}>
                        {course.title}
                    </Typography>
                </Box>

                {/* Description with fixed height and overflow handling */}
                <Typography
                    variant="body2"
                    sx={{
                        mb: 2,
                        height: '3em', // Fixed height for 2-3 lines
                        lineHeight: 1.5,
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        textOverflow: 'ellipsis',
                    }}
                >
                    {course.description || 'No description available'}
                </Typography>

                <Box sx={{display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap'}}>
                    <Chip
                        icon={<QuizIcon/>}
                        label={`${course.topic_count || 0} Topics`}
                        size="small"
                        variant="outlined"
                    />
                    <Chip
                        icon={<QuizIcon/>}
                        label={`${course.question_count || 0} Questions`}
                        size="small"
                        variant="outlined"
                    />
                    <Chip
                        icon={<TrendingUpIcon/>}
                        label={`Level ${course.level || 1}`}
                        size="small"
                        variant="outlined"
                    />
                </Box>

                {/* This grows to fill remaining space, pushing CardActions to bottom */}
                <Box sx={{flexGrow: 1}}/>

                <Typography variant="caption">
                    {course.question_count || 0} questions available
                </Typography>
            </CardContent>

            <CardActions sx={{p: 2, pt: 0, mt: 'auto'}}>
                <Box sx={{width: '100%'}}>
                    <Typography variant="caption" color="text.secondary"
                                sx={{display: 'block', mb: 1}}>
                        Join {course.student_count || 0} students
                    </Typography>
                    {isEnrolled ? (
                        <Button
                            variant="contained"
                            fullWidth
                            disabled
                            startIcon={<CheckIcon/>}
                            sx={{
                                textTransform: 'none',
                                backgroundColor: 'success.main',
                                '&.Mui-disabled': {
                                    backgroundColor: 'success.main',
                                    color: 'white',
                                    opacity: 0.8,
                                }
                            }}
                        >
                            Enrolled
                        </Button>
                    ) : (
                        <Button
                            variant="outlined"
                            fullWidth
                            onClick={() => onJoinCourse(course)}
                            disabled={isJoining}
                            sx={{
                                textTransform: 'none',
                                ...(isJoining && {
                                    animation: 'pulse 1s ease-in-out infinite',
                                    '@keyframes pulse': {
                                        '0%': {
                                            transform: 'scale(1)',
                                            opacity: 1,
                                        },
                                        '50%': {
                                            transform: 'scale(1.02)',
                                            opacity: 0.8,
                                        },
                                        '100%': {
                                            transform: 'scale(1)',
                                            opacity: 1,
                                        },
                                    },
                                }),
                            }}
                        >
                            {isJoining ? 'Joining...' : 'Join Course'}
                        </Button>
                    )}
                </Box>
            </CardActions>
        </Card>
    </Box>
);